/**
 * PaperPop Shopify Theme - Animations
 * Scroll-triggered animations and effects
 */

class ScrollAnimations {
  constructor() {
    this.elements = document.querySelectorAll('[data-animate]');
    this.observer = null;
    
    this.init();
  }

  init() {
    if (!this.elements.length) return;
    
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => this.handleIntersect(entries),
        {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px'
        }
      );

      this.elements.forEach(el => {
        el.style.opacity = '0';
        el.style.transform = 'translateY(30px)';
        el.style.transition = 'opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1), transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)';
        this.observer.observe(el);
      });
    } else {
      // Fallback
      this.elements.forEach(el => {
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
    }
  }

  handleIntersect(entries) {
    entries.forEach((entry, index) => {
      if (entry.isIntersecting) {
        const delay = entry.target.dataset.animateDelay || index * 100;
        
        setTimeout(() => {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'none';
        }, delay);
        
        this.observer.unobserve(entry.target);
      }
    });
  }
}

// Parallax effect for hero images
class ParallaxEffect {
  constructor() {
    this.elements = document.querySelectorAll('[data-parallax]');
    this.init();
  }

  init() {
    if (!this.elements.length) return;
    
    window.addEventListener('scroll', () => {
      requestAnimationFrame(() => this.update());
    }, { passive: true });
  }

  update() {
    const scrollY = window.pageYOffset;
    
    this.elements.forEach(el => {
      const speed = parseFloat(el.dataset.parallax) || 0.5;
      const yPos = -(scrollY * speed);
      el.style.transform = `translateY(${yPos}px)`;
    });
  }
}

// Counter animation
class CounterAnimation {
  constructor(element) {
    this.element = element;
    this.target = parseInt(element.dataset.counter);
    this.duration = parseInt(element.dataset.counterDuration) || 2000;
    this.suffix = element.dataset.counterSuffix || '';
    
    this.init();
  }

  init() {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.animate();
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.5 });

      observer.observe(this.element);
    }
  }

  animate() {
    const startTime = performance.now();
    const startValue = 0;

    const step = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / this.duration, 1);
      
      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.floor(startValue + (this.target - startValue) * easeOut);
      
      this.element.textContent = currentValue + this.suffix;
      
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }
}

// Stagger animation for lists
class StaggerAnimation {
  constructor(container) {
    this.container = container;
    this.items = container.querySelectorAll('[data-stagger]');
    
    this.init();
  }

  init() {
    if (!this.items.length) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.animate();
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.2 });

      observer.observe(this.container);
    }
  }

  animate() {
    this.items.forEach((item, index) => {
      item.style.opacity = '0';
      item.style.transform = 'translateY(20px)';
      item.style.transition = `opacity 0.5s ease ${index * 0.1}s, transform 0.5s ease ${index * 0.1}s`;
      
      setTimeout(() => {
        item.style.opacity = '1';
        item.style.transform = 'none';
      }, 50);
    });
  }
}

function initializePaperPopAnimations(root = document) {
  // Scroll animations
  new ScrollAnimations();
  
  // Parallax effect
  new ParallaxEffect();
  
  // Counter animations
  document.querySelectorAll('[data-counter]').forEach(el => {
    new CounterAnimation(el);
  });
  
  // Stagger animations
  document.querySelectorAll('[data-stagger-container]').forEach(container => {
    new StaggerAnimation(container);
  });
}

// Initialize all animations
document.addEventListener('DOMContentLoaded', () => initializePaperPopAnimations());
document.addEventListener('shopify:section:load', () => initializePaperPopAnimations());

// Export for global access
window.PaperPopAnimations = {
  ScrollAnimations,
  ParallaxEffect,
  CounterAnimation,
  StaggerAnimation
};
